import type { MembershipRole } from './common';

export const crudActions = ['read', 'create', 'update', 'delete'] as const;
export type CrudAction = (typeof crudActions)[number];
export type PermissionAction = CrudAction | (string & {});

export type PermissionScope = 'tenant' | 'platform' | 'both';

export interface PermissionPage {
  id: string;
  label: string;
  section: string;
  scope: PermissionScope;
  actions: readonly string[];
}

export type PermissionBag = Record<string, Record<string, boolean>>;

const crud = [...crudActions];

export const PERMISSION_CATALOG: readonly PermissionPage[] = [
  { id: 'dashboard', label: 'Dashboard', section: 'Overview', scope: 'tenant', actions: ['read'] },
  { id: 'tenants', label: 'Tenants', section: 'Overview', scope: 'platform', actions: crud },
  { id: 'users', label: 'Users', section: 'Overview', scope: 'tenant', actions: crud },
  { id: 'workspaces', label: 'Workspaces', section: 'Overview', scope: 'tenant', actions: crud },
  { id: 'groups', label: 'Groups', section: 'Overview', scope: 'both', actions: [...crud, 'manage_members'] },
  { id: 'builder', label: 'AI Trainer', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'agents', label: 'Agents', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'org', label: 'Org & Hierarchy', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'tools', label: 'Tools', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'skills', label: 'Skills', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'workflow_types', label: 'Workflow Types', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'taxonomy', label: 'Taxonomy', section: 'Design', scope: 'tenant', actions: crud },
  { id: 'triggers', label: 'Triggers', section: 'Connect', scope: 'tenant', actions: crud },
  { id: 'integrations', label: 'Integrations', section: 'Connect', scope: 'tenant', actions: crud },
  { id: 'mcp', label: 'MCP Servers', section: 'Connect', scope: 'tenant', actions: crud },
  { id: 'knowledge', label: 'Knowledge', section: 'Connect', scope: 'tenant', actions: [...crud, 'upload'] },
  {
    id: 'collaborate',
    label: 'Collaborate',
    section: 'Operate',
    scope: 'tenant',
    actions: [...crud, 'message'],
  },
  { id: 'runs', label: 'Runs', section: 'Operate', scope: 'tenant', actions: [...crud, 'execute', 'cancel'] },
  { id: 'mailbox', label: 'Mailbox', section: 'Operate', scope: 'tenant', actions: [...crud, 'execute'] },
  { id: 'payments', label: 'Payment orders', section: 'Operate', scope: 'tenant', actions: [...crud, 'execute'] },
  { id: 'review', label: 'Review', section: 'Govern', scope: 'tenant', actions: [...crud, 'decide'] },
  { id: 'approvals', label: 'Approvals', section: 'Govern', scope: 'tenant', actions: [...crud, 'decide'] },
  { id: 'evals', label: 'Evaluations', section: 'Govern', scope: 'tenant', actions: crud },
  { id: 'learning', label: 'Evolution', section: 'Govern', scope: 'tenant', actions: [...crud, 'teach'] },
  { id: 'settings', label: 'Settings', section: 'Govern', scope: 'tenant', actions: ['read', 'update'] },
  { id: 'costs', label: 'Cost Analytics', section: 'Platform', scope: 'platform', actions: ['read'] },
  {
    id: 'identity_providers',
    label: 'Identity Providers',
    section: 'Platform',
    scope: 'platform',
    actions: crud,
  },
  {
    id: 'platform_settings',
    label: 'Platform Settings',
    section: 'Platform',
    scope: 'platform',
    actions: ['read', 'update', 'create', 'delete'],
  },
];

export function tenantPagesFromCatalog(): PermissionPage[] {
  return PERMISSION_CATALOG.filter((p) => p.scope === 'tenant' || p.scope === 'both');
}

export function platformPagesFromCatalog(): PermissionPage[] {
  return PERMISSION_CATALOG.filter((p) => p.scope === 'platform' || p.scope === 'both');
}

export function catalogPage(id: string): PermissionPage | undefined {
  return PERMISSION_CATALOG.find((p) => p.id === id);
}

export function hasPermission(bag: PermissionBag | undefined, page: string, action: string): boolean {
  return bag?.[page]?.[action] === true;
}

export function mergePermissionBags(bags: PermissionBag[]): PermissionBag {
  const merged: PermissionBag = {};
  for (const bag of bags) {
    for (const [page, actions] of Object.entries(bag)) {
      merged[page] ??= {};
      for (const [action, allowed] of Object.entries(actions)) {
        if (allowed) merged[page][action] = true;
      }
    }
  }
  return merged;
}

export function grantPages(pages: readonly PermissionPage[], actions?: 'all' | 'read'): PermissionBag {
  const bag: PermissionBag = {};
  for (const page of pages) {
    bag[page.id] = {};
    const granted = actions === 'read' ? page.actions.filter((a) => a === 'read') : page.actions;
    for (const action of granted) bag[page.id][action] = true;
  }
  return bag;
}

export function grantAllPermissions(): PermissionBag {
  return grantPages(PERMISSION_CATALOG, 'all');
}

const operatorExtras: Record<string, string[]> = {
  runs: ['execute', 'cancel'],
  mailbox: ['execute'],
  payments: ['execute'],
  review: ['decide'],
  approvals: ['decide'],
  collaborate: ['message'],
  knowledge: ['upload'],
  learning: ['teach'],
};

export function impliedPermissionsForRole(role: MembershipRole | 'orqforge_admin'): PermissionBag {
  if (role === 'orqforge_admin') return grantAllPermissions();
  if (role === 'viewer') return grantPages(tenantPagesFromCatalog(), 'read');
  if (role === 'operator') {
    const bag = grantPages(tenantPagesFromCatalog(), 'read');
    for (const [page, actions] of Object.entries(operatorExtras)) {
      bag[page] ??= {};
      for (const action of actions) bag[page][action] = true;
    }
    return bag;
  }
  return grantPages(tenantPagesFromCatalog(), 'all');
}

export function actionFromHttpMethod(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
    case 'HEAD':
      return 'read';
    case 'POST':
      return 'create';
    case 'PATCH':
    case 'PUT':
      return 'update';
    case 'DELETE':
      return 'delete';
    default:
      return 'read';
  }
}
